import { useCallback, useEffect, useMemo, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { formatIDR } from "../../../utils/helpers/formatIDR";
import { getStoreQris, getStoreQrisPayload, normalizeStoreSetting } from "../../../utils/helpers/storeSetting";
import { createDynamicQrisPayload, getQrisMerchantName } from "../../../utils/helpers/qris";
import "./QRISModal.css";

const COUNTDOWN_SECONDS = 5 * 60;

const QRISModal = ({ total, storeSetting, paymentNote }) => {
  const store = normalizeStoreSetting(storeSetting);
  const qrisPayload = getStoreQrisPayload(store, "");
  const qrisImage = getStoreQris(store, "");
  const merchantName = getQrisMerchantName(qrisPayload, store?.name || "Toko");
  const [remainingSeconds, setRemainingSeconds] = useState(COUNTDOWN_SECONDS);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const dynamicQris = useMemo(() => {
    if (!qrisPayload) {
      return {
        payload: null,
        error: "Payload QRIS belum dikonfigurasi. Upload ulang QRIS statis dari menu Konfigurasi Toko.",
      };
    }

    try {
      return { payload: createDynamicQrisPayload(qrisPayload, total), error: null };
    } catch (error) {
      return { payload: null, error: error?.message || "QRIS dinamis gagal dibuat." };
    }
  }, [qrisPayload, total]);

  const resetCountdown = useCallback(() => setRemainingSeconds(COUNTDOWN_SECONDS), []);

  useEffect(() => {
    const modal = document.getElementById("qrisModal");
    if (!modal) {
      return undefined;
    }

    const handleShown = () => {
      setIsModalOpen(true);
      resetCountdown();
    };
    const handleHidden = () => setIsModalOpen(false);

    modal.addEventListener("shown.bs.modal", handleShown);
    modal.addEventListener("hidden.bs.modal", handleHidden);
    return () => {
      modal.removeEventListener("shown.bs.modal", handleShown);
      modal.removeEventListener("hidden.bs.modal", handleHidden);
    };
  }, [resetCountdown]);

  useEffect(() => {
    if (!isModalOpen || remainingSeconds <= 0) {
      return undefined;
    }

    const timer = window.setInterval(() => {
      setRemainingSeconds((seconds) => Math.max(0, seconds - 1));
    }, 1000);
    return () => window.clearInterval(timer);
  }, [isModalOpen, remainingSeconds]);

  useEffect(() => {
    resetCountdown();
  }, [total, qrisPayload, resetCountdown]);

  const isExpired = remainingSeconds <= 0;
  const minutes = Math.floor(remainingSeconds / 60);
  const seconds = String(remainingSeconds % 60).padStart(2, "0");
  const progress = (remainingSeconds / COUNTDOWN_SECONDS) * 100;

  return (
    <div
      className="modal modal-blur fade akas-qris-modal"
      id="qrisModal"
      tabIndex="-1"
      role="dialog"
      aria-hidden="true"
      data-bs-backdrop="static"
      data-bs-keyboard="false">
      <div className="modal-dialog modal-dialog-centered modal-lg" role="document">
        <div className="modal-content">
          <div className="akas-qris-header">
            <div className="akas-qris-brand-icon" aria-hidden="true">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                <path d="M4 5a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v4a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V5zM14 5a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v4a1 1 0 0 1-1 1h-4a1 1 0 0 1-1-1V5zM4 15a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v4a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1v-4zM14 14h3M20 14v.01M14 17v3M17 17h3M20 17v3" />
              </svg>
            </div>
            <div className="flex-fill">
              <div className="akas-qris-eyebrow">PEMBAYARAN DIGITAL</div>
              <h3>Bayar dengan QRIS</h3>
            </div>
            <button type="button" className="btn-close btn-close-white" data-bs-dismiss="modal" aria-label="Tutup" />
          </div>

          <div className="modal-body akas-qris-content ">
            <div className="row g-4 align-items-center">
              <div className="col-md-7 text-center d-flex flex-column align-items-center justify-content-center">
                <div className="akas-qris-merchant-pill">
                  <span className="status-dot status-dot-animated bg-success" />
                  <span>{merchantName}</span>
                </div>

                {dynamicQris.payload ? (
                  <div className={`akas-qris-code-card ${isExpired ? "is-expired" : ""}`}>
                    <div className="akas-qris-code-image">
                      <QRCodeSVG
                        value={dynamicQris.payload}
                        size={330}
                        level="M"
                        marginSize={2}
                        fgColor="#0B2D32"
                        bgColor="#FFFFFF"
                        title={`QRIS ${merchantName} sebesar ${formatIDR(total)}`}
                        style={{ width: "min(42vh, 76vw)", height: "auto", maxWidth: "100%" }}
                      />
                    </div>
                    <div className="akas-qris-auto-badge">NOMINAL OTOMATIS</div>

                    {isExpired ? (
                      <div className="akas-qris-expired-overlay">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                          <circle cx="12" cy="12" r="9" />
                          <path d="M12 7v5l3 2" />
                        </svg>
                        <strong>Waktu tampilan habis</strong>
                        <button type="button" className="btn btn-primary btn-sm" onClick={resetCountdown}>
                          Perbarui QR
                        </button>
                      </div>
                    ) : null}
                  </div>
                ) : (
                  <div className="akas-qris-unavailable">
                    <div className="akas-qris-unavailable-icon">!</div>
                    <strong>QRIS dinamis belum tersedia</strong>
                    <span>{dynamicQris.error}</span>
                    {qrisImage ? <img src={qrisImage} alt={`QRIS statis ${store?.name || "Toko"}`} /> : null}
                  </div>
                )}
              </div>

              <div className="col-md-5">
                <div className="akas-qris-total-card">
                  <span>Total pembayaran</span>
                  <strong>{formatIDR(total)}</strong>
                  {dynamicQris.payload ? <small>Nominal sudah tertanam di dalam QR</small> : null}
                </div>
                {paymentNote ? <p className="small text-muted mt-2">{paymentNote}</p> : null}

                <div className="akas-qris-countdown">
                  <div>
                    <span>BATAS WAKTU TAMPILAN</span>
                    <strong className={isExpired ? "text-danger" : ""}>
                      {minutes}:{seconds}
                    </strong>
                  </div>
                  <div className="akas-qris-progress">
                    <i style={{ width: `${progress}%` }} className={isExpired ? "is-expired" : ""} />
                  </div>
                </div>

                <h4 className="akas-qris-instruction-title">Cara pembayaran</h4>
                {[
                  "Buka aplikasi bank atau dompet digital.",
                  "Pilih Scan QRIS dan arahkan kamera ke kode.",
                  "Pastikan nama merchant dan nominal benar, lalu bayar.",
                ].map((step, index) => (
                  <div className="akas-qris-step" key={step}>
                    <span>{index + 1}</span>
                    <p>{step}</p>
                  </div>
                ))}

                <div className="akas-qris-secure-note">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                    <path d="M12 3l7 3v5c0 5-3 8-7 10-4-2-7-5-7-10V6l7-3z" />
                    <path d="M9 12l2 2 4-4" />
                  </svg>
                  <span>
                    Pastikan aplikasi pembayaran menampilkan <strong>{merchantName}</strong>.
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div className="modal-footer akas-qris-footer">
            <span>Payload QRIS berasal dari konfigurasi toko ini</span>
            <button type="button" className="btn btn-outline-primary" data-bs-dismiss="modal">
              Tutup
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QRISModal;
